import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedFormItemId, reorderElementReference, transferElementReference, pushFormActionStack, reorderUndoElementReference, transferUndoRedoElementReference } from '../../../../../../stateManager/store/formCreation_v2/actions'
import { ReactComponent as FolderCloseIcon } from "../../../../../../assets/svg/solobuilder/folderclose.svg";
import { ReactComponent as AssetIcon } from "../../../../../../assets/svg/solobuilder/assetItem.svg";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "../../types/ItemTypes";
import { myRedoStack, myUndoStack } from "../../stack/Stack";
import { setCondElVisibility } from "../../helpers";
import { MouseHoverOnTree } from "../../../../../../helpers/RxServices";
import { getHighlightedText } from "../../helpers/assetHelpers";
import { ReactComponent as CloseEye } from "../../../../../../assets/svg/closeEye_icon.svg"
import { ReactComponent as DeleteIcon } from "../../../../../../assets/images/form_icons/deleteIcon.svg"
import classNames from "classnames";
import "../panel.css"

export const LeafNode = (props: any) => {
  const { item, isCheckedIn, selectedElementId, handleDelete, parentHoveredElId } = props
  const deviceType = useSelector((state: any) => state.formJson.deviceType)
  const newStyles: { [key: string]: any } = {
    ...item?.properties?.styles?.common,
    ...item?.properties?.styles?.[deviceType],
  };
  const textRef = useRef<HTMLDivElement>(null);
  const [textWidth, setTextWidth] = useState(0);
  const { isAsset, isChildAsset } = item
  const [hoveredElId, setHoveredElId] = useState("")
  const [labelValue, setLabelValue] = useState();
  const { isCoreV3, components } = useSelector((state: any) => state.formJson);
  const dispatch = useDispatch()
  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.TREE_NODE, json: { ...props.item, index: props.index } },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: props.isCheckedIn,
  });

  const [{ isOverCurrent }, drop] = useDrop({
    accept: [
      ItemTypes.TREE_NODE,
    ],
    canDrop: (_item: any) => {
      if (isAsset || isChildAsset){
        return false;
      }
      return true
    },
    collect: (monitor) => {
      return {
        isOverCurrent: monitor.isOver({ shallow: true }),
      };
    },
    drop: (item: any) => {
      if (isOverCurrent) {
        const { json } = item
        const { id, parentId } = props.item
        const dropTarget = {
          index: props.index,
          id,
          parentId
        }
        const dragSource = {
          id: json.id,
          index: json.index,
          parentId: json.parentId,
        }

        // Prevent drop element into an asset
        if (isAsset || isChildAsset){
          return;
        }

        // Prevent drop element is not a tab inside tab
        const isTab = components[dropTarget.parentId]?.type === "Tab";
        if (isTab && item.json.type === 'Tab') {
          return;
        }

        const isTabbar = components[dragSource?.parentId]?.type === "TabBar";
        if (isTabbar && item.json.type !== 'Tab') {
          return;
        }

        const isNotTab = components[dropTarget?.id]?.type === "TabBar";
        if (isNotTab && item.json.type !== 'Tab') {
          return;
        }
        // If parent gets dropped on its own child, ignore action
        if (dragSource.id === dropTarget.parentId) {
          return
        }

        // If dragged item and dropped item are the same, ignore action
        if (dragSource.id === dropTarget.id) {
          return
        }

        // If drag and drop locations are on the same parent, reorder them
        if (dropTarget.parentId === dragSource.parentId) {
          dispatch(reorderElementReference({
            dropTarget,
            dragSource,
          }))
          // Undo-Redo
          const payload: any = { dropTarget, dragSource }
          myUndoStack.push({ ...payload });
          dispatch(pushFormActionStack({
            undo: () => {
              const poppedElement = myUndoStack.pop();
              if (poppedElement) {
                myRedoStack.push(poppedElement);
                dispatch(reorderUndoElementReference({
                  dropTarget: {
                    index: poppedElement.dragSource.index,
                    id: "",
                    parentId: poppedElement.dragSource.parentId,
                  },
                  dragSource: {
                    ...poppedElement.dragSource,
                    index: poppedElement.dropTarget.index,
                    parentId: poppedElement.dropTarget.parentId,
                  },
                  increment: -1
                }))
              }
            },
            redo: () => {
              const poppedElement = myRedoStack.pop();
              if (poppedElement) {
                myUndoStack.push({ ...poppedElement });
                dispatch(reorderUndoElementReference({
                  dropTarget: poppedElement.dropTarget,
                  dragSource: poppedElement.dragSource,
                  increment: 1
                }))
              }
            }
          }));
        }

        // If drag and drop locations are from different parents, transfer them
        if (dropTarget.parentId !== dragSource.parentId) {
          const payload: any = {
            id: json.id,
            dragSourceId: dragSource.parentId,
            dropTargetId: dropTarget.parentId,
            index: dropTarget.index
          }

          // Undo-Redo
          myUndoStack.push({ ...payload });
          dispatch(transferElementReference({
            id: json.id,
            dragSourceId: dragSource.parentId,
            dropTargetId: dropTarget.parentId,
            index: dropTarget.index
          }))

          dispatch(pushFormActionStack({
            undo: () => {
              const poppedElement = myUndoStack.pop();
              if (poppedElement) {
                myRedoStack.push(poppedElement);
                dispatch(transferUndoRedoElementReference({
                  id: poppedElement.id,
                  dragSourceId: poppedElement.dropTargetId,
                  dropTargetId: poppedElement.dragSourceId,
                  index: dragSource.index,
                  increment: -1
                }))
              }
            },
            redo: () => {
              const poppedElement = myRedoStack.pop();
              if (poppedElement) {
                myUndoStack.push({ ...poppedElement });
                dispatch(transferUndoRedoElementReference({
                  id: poppedElement.id,
                  dragSourceId: poppedElement.dragSourceId,
                  dropTargetId: poppedElement.dropTargetId,
                  index: poppedElement.index,
                  increment: 1
                }))
              }
            }
          }));
        }
      }
    },
  });

  const subscription = MouseHoverOnTree.getMessage().subscribe(function (e) {
    setHoveredElId(e);
  });

  useEffect(() => {
    if (!subscription) {
      MouseHoverOnTree.getMessage();
    }

    return () => {
      subscription.unsubscribe();
    };
  }, [hoveredElId]);

  const onMouseEnterOnTree = (e: any, id: string) => {
    setCondElVisibility(item.id, props.json.components)
    MouseHoverOnTree.sendMessage(item.id)
  }

  const onMouseLeaveTree = () => {
    parentHoveredElId ? MouseHoverOnTree.sendMessage(parentHoveredElId) : MouseHoverOnTree.sendMessage(null)
  }

  const onSelectingNode = (e: any) => {
    e.stopPropagation();
    setCondElVisibility(item.id, props.json.components)
    dispatch(setSelectedFormItemId({
      id: item.id,
      isAssetInstanceSelected: isAsset || isChildAsset,
    }))
  }
  const wrapperStyles: React.CSSProperties = {
    opacity: isDragging ? 0.4 : 1,
    paddingTop: isOverCurrent ? '6px' : undefined,
    backgroundColor: hoveredElId === item.id ? "#EFF0F4" : ""
  }

  const modifiedLabel = () => {
    let label = item.type && item.type.toLowerCase() + '-' + item.id;
    if (props?.searchValue) {
      return getHighlightedText(label, props?.searchValue)
    } else {
      return label
    }
  }

  const onMouseClick=(e: React.MouseEvent<HTMLParagraphElement, MouseEvent>)=>{
    e.stopPropagation();
    setCondElVisibility(item.id, props.json.components)
    dispatch(setSelectedFormItemId({
      id: item.id,
      isAssetInstanceSelected: isAsset || isChildAsset,
    }))
  }

useEffect(() => {
    const label = modifiedLabel();
    setLabelValue(label);
  }, [props?.searchValue]);

  useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.scrollWidth);
    }
  }, [labelValue]);
  const dynamicWidth = isCoreV3 && (Math.min(props?.scrollInfo.containerWidth + props?.scrollInfo.scrollLeft, props?.scrollInfo.scrollWidth) - props?.ChildOffSetLeft) - 50;

  return (
    <>
      {isCoreV3 ?
        <div
          key={item.id}
          ref={(ref) => drag(drop(ref))}
          className={classNames(
            "cursor-pointer flex items-center gap-2 py-1 justify-between", 
            { "opacity-40": isDragging, "opacity-100": !isDragging }, 
            { "pt-1.5": isOverCurrent }, 
            { "bg-highlight_onhover": hoveredElId === item.id },
            { "tree-drop-leaf": isOverCurrent }, 
            { "text-hdb-blue": isCheckedIn && selectedElementId === item.id } 
          )}
          title={labelValue}
          style={{ width: `${props?.parentWidth}px` }}
          onMouseEnter={(e) => onMouseEnterOnTree(e, item.id)}
          onMouseLeave={onMouseLeaveTree}
        >
          <div className="flex items-center gap-2 py-1 overflow-hidden" style={{ minWidth: textWidth + 62 }}>
            <div>
              {isAsset ? <AssetIcon /> :<FolderCloseIcon />}
            </div>
            <p key={item.id}
              className={`${!newStyles.visible || hoveredElId === item.id ? 'truncate' : ''} truncateText`}
              ref={textRef}
              style={{ minWidth: "40px", maxWidth:  !newStyles.visible || hoveredElId === item.id ? `${dynamicWidth}px` : "unset" }}
              onClick={onMouseClick} title={labelValue} data-test-id={labelValue}>
              {labelValue}
            </p>
          </div>
          {
            ((!newStyles.visible && dynamicWidth > 34) || (newStyles.visible && dynamicWidth > 18)) &&
          <div className="flex sticky right-0">
            {!newStyles.visible && <div className={`mr-2`}><CloseEye width={13} height={13} title="You've hidden this widget"/></div>}
            {(hoveredElId === item.id && !isChildAsset) && <div className={`mr-2`}><DeleteIcon onClick={(e) => handleDelete(e)} width={13} height={13} title="Delete Widget" /></div>}
          </div>
          }
        </div>
        :
        <div key={item.id} style={wrapperStyles} ref={ref => drag(drop(ref))}
          className={`cursor-pointer flex items-center gap-2 py-1 justify-between ${isOverCurrent ? 'tree-drop-leaf' : ''}${(isCheckedIn && selectedElementId === item.id ? 'text-hdb-blue' : '')}`}
        >
          <div className={`flex items-center gap-2 py-1`}><FolderCloseIcon />
            <p key={item.id} className="max-w-127 truncate" onClick={e => onSelectingNode(e)} >
            {labelValue}
          </p>
          </div>
          <div className="flex">
            {hoveredElId === item.id && <div className={`mr-2`}><DeleteIcon onClick={(e) => handleDelete(e)} width={13} height={13} /></div>}
            {!newStyles.visible && <div className={`mr-2`}><CloseEye width={13} height={13} /></div>}
          </div>
        </div>
      }
    </>

  )
}